import React from 'react';
import { CostGapTag, gapKindForReason, type CostGapKind } from '../CostGapTag';
import type { IncomeStatementPurchase } from './incomeStatementApi';

// Labels of one income statement purchase row, shared by „Źródła zakupów firmy”
// and „Prognoza a wynik”. Generated draft ZD lines without a purchase price
// use the average purchase cost; explicitly priced lines retain their value.

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

const SOURCES = 'reports:profitability.incomeStatement.purchaseSources';

export function purchaseRowSourceLabel(row: IncomeStatementPurchase, t: TranslateFn): string {
  if (!row.draft) return row.supplierName;
  return row.draft.sourceOrderNumber
    ? t(`${SOURCES}.draftAverage`, { order: row.draft.sourceOrderNumber })
    : t(`${SOURCES}.draftAverageNoSource`);
}

/** Purchase amount with extra costs; null when nothing of it is known (a draft without any average). */
export function purchaseRowAmount(row: IncomeStatementPurchase): number | null {
  if (row.netPLN == null) return row.extraCostPLN ? row.extraCostPLN : null;
  return row.netPLN + row.extraCostPLN;
}

/** Why part of a draft row is not counted: one label per WP5 reason (missing average, unit, rate). */
export function purchaseRowGapKinds(row: IncomeStatementPurchase): CostGapKind[] {
  if (!row.draft?.costPending) return [];
  const kinds = row.draft.pendingReasons
    .map((reason) => gapKindForReason(reason))
    .filter((kind): kind is CostGapKind => kind != null);
  const unique = kinds.filter((kind, index) => kinds.indexOf(kind) === index);
  return unique.length ? unique : ['NO_AVERAGE_PURCHASE_COST'];
}

export function PurchaseRowCostGap({ row, t }: { row: IncomeStatementPurchase; t: TranslateFn }) {
  const kinds = purchaseRowGapKinds(row);
  if (!kinds.length) return null;
  return <>{kinds.map((kind) => <React.Fragment key={kind}> <CostGapTag kind={kind} t={t} /></React.Fragment>)}</>;
}
